import { useEffect, useMemo, useState } from "react";
import { RefreshCcw } from "lucide-react";
import toast from "react-hot-toast";
import { DefaultButton } from "../buttons/DefaultButton";
import { ModalWrapper } from "../modals/ModalWrapper";
import { useAuth } from "../../context/AuthContext";
import { Country } from "../../const/countryList";
import { dictionary } from "../../utils/dictionary";
import { handleApi } from "../../utils/handleApi";
import { CustomerInterface } from "../../pages/OrderDetailsPage/OrderDetailsInterface";
import { DropiState } from "../../pages/SalesPage/SalesPageInterface";
import {
  retryDropiOrder,
  updateOrderCustomer,
} from "../../services/orders/orderAxios";
import {
  DropiCity,
  DropiDepartment,
  getDropiCitiesByDepartmentId,
  getDropiDepartments,
} from "../../services/orders/dropiLocationAxios";

interface DropiRetryCardProps {
  orderId: number;
  dropiState?: DropiState;
  customer: CustomerInterface;
  error?: string;
  onSynced?: () => void;
  /** Renderiza solo un botón-link que abre el mismo modal (sin la card). */
  compact?: boolean;
}

interface RetryForm {
  name: string;
  lastName: string;
  phone: string;
  email: string;
  address: string;
  addressDetail: string;
  neighborhood: string;
  department: string;
  city: string;
}

const emptyForm: RetryForm = {
  name: "",
  lastName: "",
  phone: "",
  email: "",
  address: "",
  addressDetail: "",
  neighborhood: "",
  department: "",
  city: "",
};

const normalize = (value: string | undefined | null) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const optionExists = (options: { name: string }[], value: string) =>
  options.some((option) => normalize(option.name) === normalize(value));

const withCurrentOption = <T extends { id: number; name: string }>(
  options: T[],
  value: string
): Array<T | { id: number; name: string }> => {
  if (!value || optionExists(options, value)) {
    return options;
  }
  return [{ id: -1, name: value }, ...options];
};

export const DropiRetryCard = ({
  orderId,
  dropiState,
  customer,
  error,
  onSynced,
  compact = false,
}: DropiRetryCardProps) => {
  const { language, country } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [departments, setDepartments] = useState<DropiDepartment[]>([]);
  const [cities, setCities] = useState<DropiCity[]>([]);
  const [form, setForm] = useState<RetryForm>(emptyForm);

  const countryCode = country || Country.COLOMBIA;
  const isPending = dropiState === DropiState.PENDING_SYNC;
  const description = isPending
    ? dictionary["integration-dropi-description-pending"]?.[language] ||
      "La orden esta pendiente de sincronizacion con Dropi."
    : dictionary["integration-dropi-description-error"]?.[language];
  const buttonText =
    dictionary["integration-dropi-retry-with-address"]?.[language] ||
    "Editar y sincronizar";

  const departmentOptions = useMemo(
    () => withCurrentOption(departments, form.department),
    [departments, form.department]
  );
  const cityOptions = useMemo(
    () => withCurrentOption(cities, form.city),
    [cities, form.city]
  );

  const setField = (field: keyof RetryForm, value: string) => {
    setForm((current) => ({
      ...current,
      [field]: field === "phone" ? value.replace(/\D/g, "") : value,
      ...(field === "department" ? { city: "" } : {}),
    }));
  };

  const hydrateForm = () => {
    setForm({
      name: customer.name || "",
      lastName: customer.last_name || "",
      phone: customer.phone || "",
      email: customer.email || "",
      address: customer.address || "",
      addressDetail: customer.address_detail || "",
      neighborhood: customer.neighborhood || "",
      department: customer.department || "",
      city: customer.city || "",
    });
  };

  const openModal = () => {
    hydrateForm();
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;

    const fetchDepartments = async () => {
      setIsLoadingDepartments(true);
      const { res } = await handleApi(getDropiDepartments(countryCode), false);
      if (res) {
        setDepartments(res);
      }
      setIsLoadingDepartments(false);
    };

    fetchDepartments();
  }, [isOpen, countryCode]);

  useEffect(() => {
    if (!isOpen || departments.length === 0 || !form.department) {
      setCities([]);
      return;
    }

    const selectedDepartment = departments.find(
      (department) => normalize(department.name) === normalize(form.department)
    );

    if (!selectedDepartment) {
      setCities([]);
      return;
    }

    const fetchCities = async () => {
      setIsLoadingCities(true);
      const { res } = await handleApi(
        getDropiCitiesByDepartmentId(selectedDepartment.id, countryCode),
        false
      );
      if (res) {
        setCities(res);
      }
      setIsLoadingCities(false);
    };

    fetchCities();
  }, [isOpen, departments, form.department, countryCode]);

  const validateForm = () => {
    const requiredFields: Array<[keyof RetryForm, string]> = [
      ["name", dictionary["name"]?.[language] || "Nombre"],
      ["phone", dictionary["phone-label"]?.[language] || "Telefono"],
      ["email", dictionary["email-label"]?.[language] || "Correo"],
      ["address", dictionary["shipping-address"]?.[language] || "Direccion"],
      ["department", dictionary["department"]?.[language] || "Departamento"],
      ["city", dictionary["city"]?.[language] || "Ciudad"],
    ];

    const emptyField = requiredFields.find(([field]) => !form[field].trim());
    if (emptyField) {
      toast.error(`${emptyField[1]} es obligatorio`);
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error(dictionary["email-invalid"]?.[language] || "Correo invalido");
      return false;
    }

    return true;
  };

  const handleRetry = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    const { error: updateError } = await handleApi(
      updateOrderCustomer(orderId, {
        name: form.name.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        addressDetail: form.addressDetail.trim(),
        neighborhood: form.neighborhood.trim(),
        department: form.department.trim(),
        city: form.city.trim(),
        source: "dropi_manual_retry",
      })
    );

    if (!updateError) {
      const { error: retryError } = await handleApi(retryDropiOrder(orderId));
      if (!retryError) {
        toast.success(
          dictionary["dropi-retry-success"]?.[language] ||
            "Orden enviada nuevamente a Dropi"
        );
        setIsOpen(false);
        onSynced?.();
      }
    }
    setIsSubmitting(false);
  };

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={openModal}
          className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs font-semibold text-fluxi transition-colors hover:text-fluxi-dark"
        >
          <RefreshCcw className="h-3.5 w-3.5" />
          {buttonText}
        </button>
      ) : (
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <h2 className="text-lg font-semibold mb-4">
          {dictionary["integration-dropi"][language]}
        </h2>
        <div className="space-y-2 text-gray-700">
          <p>{description}</p>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex justify-end pt-4">
          <DefaultButton
            title={buttonText}
            onClick={openModal}
            isLoading={false}
            iconLeft={<RefreshCcw className="w-4 h-4" />}
            showIcon={false}
            fromColor="#059669"
            toColor="#059669"
            fromHoverColor="#047857"
            toHoverColor="#047857"
          />
        </div>
      </div>
      )}

      {isOpen && (
        <ModalWrapper isOpen onClose={() => setIsOpen(false)}>
          <div className="p-6">
            <h3 className="text-lg font-semibold pr-8">
              {dictionary["dropi-retry-modal-title"]?.[language] ||
                "Editar envio y sincronizar"}
            </h3>
            <div className="mt-5 grid grid-cols-1 gap-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField
                  label={dictionary["name"]?.[language] || "Nombre"}
                  value={form.name}
                  onChange={(value) => setField("name", value)}
                />
                <TextField
                  label={dictionary["last-name"]?.[language] || "Apellido"}
                  value={form.lastName}
                  onChange={(value) => setField("lastName", value)}
                />
              </div>

              <TextField
                label={dictionary["phone-label"]?.[language] || "Telefono"}
                value={form.phone}
                onChange={(value) => setField("phone", value)}
              />

              <TextField
                label={dictionary["email-label"]?.[language] || "Correo"}
                value={form.email}
                onChange={(value) => setField("email", value)}
                type="email"
              />

              <TextField
                label={dictionary["shipping-address"]?.[language] || "Direccion"}
                value={form.address}
                onChange={(value) => setField("address", value)}
              />

              <TextField
                label={dictionary["address-detail"]?.[language] || "Detalle"}
                value={form.addressDetail}
                onChange={(value) => setField("addressDetail", value)}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SelectField
                  label={dictionary["department"]?.[language] || "Departamento"}
                  value={form.department}
                  onChange={(value) => setField("department", value)}
                  loading={isLoadingDepartments}
                  options={departmentOptions}
                />
                <SelectField
                  label={dictionary["city"]?.[language] || "Ciudad"}
                  value={form.city}
                  onChange={(value) => setField("city", value)}
                  loading={isLoadingCities}
                  options={cityOptions}
                  disabled={!form.department || isLoadingCities}
                />
              </div>

              <TextField
                label={dictionary["neighborhood"]?.[language] || "Barrio"}
                value={form.neighborhood}
                onChange={(value) => setField("neighborhood", value)}
              />
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                {dictionary["cancel"]?.[language]}
              </button>
              <DefaultButton
                title={
                  dictionary["reintegrate-dropi"]?.[language] ||
                  "Reintegrar a Dropi"
                }
                onClick={handleRetry}
                isLoading={isSubmitting}
                disabled={
                  isLoadingDepartments || isLoadingCities || isSubmitting
                }
                iconLeft={<RefreshCcw className="w-4 h-4" />}
                showIcon={false}
                fromColor="#059669"
                toColor="#059669"
                fromHoverColor="#047857"
                toHoverColor="#047857"
                className="py-2"
              />
            </div>
          </div>
        </ModalWrapper>
      )}
    </>
  );
};

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}

const TextField = ({ label, value, onChange, type = "text" }: TextFieldProps) => (
  <label className="block">
    <span className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </span>
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  </label>
);

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ id: number; name: string }>;
  loading?: boolean;
  disabled?: boolean;
}

const SelectField = ({
  label,
  value,
  onChange,
  options,
  loading,
  disabled,
}: SelectFieldProps) => (
  <label className="block">
    <span className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </span>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled || loading}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
    >
      <option value="">{loading ? "Cargando..." : "Selecciona"}</option>
      {options.map((option) => (
        <option key={`${option.id}-${option.name}`} value={option.name}>
          {option.name}
        </option>
      ))}
    </select>
  </label>
);
